import { z } from 'zod';
import { isValidSlug, isValidUsername } from './ids.js';
import { isValidPackageName, normalizePackageName } from './prefix.js';
/**
 * Vocabulary shared by every endpoint. These enums are the same strings that appear in the
 * D1 CHECK constraints, so a schema change and a migration change are always visibly paired.
 */
export const TeamRole = z.enum(['owner', 'admin', 'developer', 'tester']);
/** Ranking used for "developer or above" checks. Higher is more privileged. */
export const ROLE_RANK = {
    tester: 0,
    developer: 1,
    admin: 2,
    owner: 3,
};
export function roleAtLeast(role, minimum) {
    return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
/**
 * The two things a Board runs, named the way Board's own documentation names them: a Unity
 * build, and a web app. Not `android`, which was this system's word for the first of them and
 * was wrong twice over — it named an operating system where Board names a toolchain, and it
 * described the artifact rather than the thing a developer chose when they made the game.
 *
 * Migration 0013 renamed the stored value; nothing accepts `android` any more.
 *
 * `unity` also appears in `BuildSource` below, and the two are not the same claim: this one is
 * what the game *is* and never changes, that one is which tool pushed a particular build — a
 * Unity game uploaded from CI has platform `unity` and source `cli`.
 */
export const Platform = z.enum(['unity', 'web']);
/**
 * `patching` is the in-game feedback layer's (docs/feedback-plan.md §3.3): the bytes are
 * uploaded and checked, and a runner is adding the layer to them. It sits between `uploading`
 * and `ready` and is as invisible to a tester as `uploading` is — a build that fails patching
 * never reaches `ready`, and never consumes a build number.
 */
export const BuildStatus = z.enum(['uploading', 'patching', 'ready', 'failed']);
/**
 * Whose signature the published bytes carry (docs/feedback-plan.md §3.4).
 *
 *   developer    the uploader's own signature, untouched — every build that is not patched
 *   boardflight  re-signed with a key BoardFlight generated for this game, which costs testers
 *                one uninstall the first time the layer is turned on
 *   team         re-signed with the team's own uploaded keystore, which for a tester is
 *                indistinguishable from `developer` and is the point of offering it
 *
 * It lives here, beside `BuildStatus`, because it is a property of a build rather than of the
 * feedback layer — the layer is only the reason a build ever stops being `developer`.
 */
export const SigningIdentity = z.enum(['developer', 'boardflight', 'team']);
export const BuildSource = z.enum(['unity', 'godot', 'web', 'cli', 'dashboard']);
/**
 * What a session was opened by. `device` is whatever signed in with a device code and is not an
 * editor: it was the Board client's, and it is the desktop app's now (`docs/off-board-plan.md` §3).
 *
 * There is deliberately no `desktop` kind. `sessions.kind` and `device_auth.kind` carry a CHECK
 * over these three (migration 0001), and SQLite widens a CHECK only by rebuilding the table. What
 * makes a session a desktop app's is its row in `desktops`, which is what `/v1/desktop` asks for.
 */
export const SessionKind = z.enum(['web', 'device', 'plugin']);
export const InstallStatus = z.enum(['started', 'downloaded', 'installed', 'failed']);
export const Slug = z
    .string()
    .transform((s) => s.trim().toLowerCase())
    .refine(isValidSlug, {
    message: 'Must be 2-64 characters: lowercase letters, digits and single hyphens.',
});
export const PackageName = z
    .string()
    .transform(normalizePackageName)
    .refine(isValidPackageName, {
    message: 'Must be a dot-separated package name such as "com.acme.game".',
});
export const Ulid = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, 'Not a ULID.');
/** SHA-256 as lowercase hex — computed by the uploader, checked at finalize, re-verified on the device. */
export const Sha256 = z
    .string()
    .transform((s) => s.trim().toLowerCase())
    .refine((s) => /^[0-9a-f]{64}$/.test(s), 'Must be a hex-encoded SHA-256 digest.');
/**
 * A person's real name. Optional everywhere it is collected and served only to people who share a
 * team with them — `Username` is what everybody else gets. See `apps/api/src/lib/identity.ts`.
 *
 * Still the schema for a *team's* name, which is public to its testers and always present.
 */
export const DisplayName = z.string().trim().min(1).max(120);
/**
 * The handle an account is known by. Unique across the system, case-insensitively: lowercased on
 * the way in so the stored value and the one a client renders are the same string.
 */
export const Username = z
    .string()
    .trim()
    .toLowerCase()
    .refine(isValidUsername, {
    message: 'Must be 3-32 characters of lowercase letters, digits, hyphens or underscores, starting and ending with a letter or digit.',
});
/**
 * How another account appears in any list that names people.
 *
 * `username` is always there. `displayName` and `email` are there only when the person reading the
 * response shares a team with the person it describes; for everybody else they are null and the
 * response is shaped exactly as it would be if the account had never given a real name. The rule
 * lives in `apps/api/src/lib/identity.ts` and clients must not infer anything from a null beyond
 * "not shown" — in particular, a null `displayName` is not evidence that a name was never set.
 */
export const UserIdentity = z.object({
    userId: Ulid,
    username: z.string(),
    displayName: z.string().nullable(),
    email: z.string().nullable(),
});
/**
 * Every non-2xx response has this shape. `code` is stable and machine-readable; `message`
 * is for humans and may change. `details` carries field-level validation errors.
 */
export const ApiError = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
export const Paginated = (item) => z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=common.js.map